import type { ReactNode } from 'react';

import { Logo } from '@/components/ui/Logo';

/**
 * Left-side branding panel for the auth screens (Login + Signup). Dark
 * ink-colored background with a dot-pattern texture, a hero title in
 * Fraunces italic, an explainer sub, and a testimonial card pinned to
 * the bottom. v2 design — replaces the previous flame-gradient panel.
 */
export type AuthTestimonial = {
  q: string;
  n: string;
  t: string;
  initials: string;
  /** Avatar background (oklch or hex). */
  color: string;
};

export function AuthBrandSide({
  title,
  sub,
  testimonial,
}: {
  title: ReactNode;
  sub: string;
  testimonial: AuthTestimonial;
}) {
  return (
    <div
      style={{
        flex: 1,
        background: 'var(--ink)',
        color: 'white',
        padding: '48px 56px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Dot pattern background */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none' }} aria-hidden>
        <svg width="100%" height="100%">
          <pattern id="authdot" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="white" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#authdot)" />
        </svg>
      </div>

      {/* Wordmark */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Logo size={22} />
        <span style={{ fontWeight: 600, fontSize: 15 }}>Emailforge</span>
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: 'oklch(0.65 0.01 85)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            marginLeft: 4,
          }}
        >
          EmailOps · for developers
        </span>
      </div>

      {/* Hero title + sub */}
      <div style={{ position: 'relative', marginTop: 'auto', marginBottom: 'auto' }}>
        <h2
          className="display"
          style={{ fontSize: 54, letterSpacing: -0.025, lineHeight: 1.04, color: 'white', margin: 0 }}
        >
          {title}
        </h2>
        <div
          style={{
            fontSize: 16,
            color: 'oklch(0.75 0.01 85)',
            marginTop: 18,
            maxWidth: 420,
            lineHeight: 1.55,
          }}
        >
          {sub}
        </div>
      </div>

      {/* Testimonial card */}
      <div
        style={{
          position: 'relative',
          padding: '20px 22px',
          border: '1px solid oklch(0.28 0.005 85)',
          borderRadius: 8,
          background: 'oklch(0.15 0.005 85 / 0.6)',
        }}
      >
        <div style={{ fontSize: 13.5, color: 'oklch(0.88 0.01 85)', lineHeight: 1.55 }}>
          "{testimonial.q}"
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: testimonial.color,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10.5,
              fontWeight: 600,
            }}
          >
            {testimonial.initials}
          </div>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 550 }}>{testimonial.n}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'oklch(0.65 0.01 85)' }}>
              {testimonial.t}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
